/**
 * Shared API-POI to component-POI converter used by LocationsPage and SystemsPage.
 * Extracted from the near-identical inline mappings in both pages (DUP-18).
 * Page-specific bits (category mapping, subCategory extraction, color, extra
 * properties) are passed as options; each page keeps its own behavior.
 */

export interface ConvertApiPoiOptions {
  /** Map the API POI to the component category. Default: apiPoi.ifc_category || 'Unknown'. */
  mapCategory?: (apiPoi: any) => any;
  /** Compute properties.subCategory. Default: apiPoi.subcategory || ''. */
  extractSubCategory?: (apiPoi: any) => string;
  /** Compute visualization.color. Default: '#48CAE4'. */
  getColor?: (apiPoi: any) => string;
  /** Extra fields merged into properties (e.g., system hierarchy fields). */
  extraProperties?: Record<string, any>;
}

export const convertApiPoiToComponent = (apiPoi: any, options: ConvertApiPoiOptions = {}): any => ({
  id: apiPoi.poi_id || apiPoi.id,
  // Raw DB PK (SERIAL) — `id` above prefers the EXTERNAL poi_id string; callers
  // addressing server routes (thumbnails etc.) must use dbId, never id.
  dbId: apiPoi.id,
  name: apiPoi.title || 'Unnamed Asset',
  category: options.mapCategory ? options.mapCategory(apiPoi) : (apiPoi.ifc_category || 'Unknown'),
  coordinates: {
    x: parseFloat(apiPoi.x || '0'),
    y: parseFloat(apiPoi.y || '0'),
    z: parseFloat(apiPoi.z || '0')
  },
  // Include ifc_source_file at top level for WebIfcViewer navigation
  ifc_source_file: apiPoi.ifc_source_file || undefined,
  source_file: apiPoi.ifc_source_file || undefined,
  // Preserve custom_data for RVT POI navigation (contains rvtElementDbId)
  custom_data: apiPoi.custom_data || undefined,
  properties: {
    description: (() => {
      try {
        return apiPoi.custom_data ? JSON.parse(apiPoi.custom_data).description || '' : '';
      } catch {
        return '';
      }
    })(),
    type: apiPoi.poi_type_name || apiPoi.ifc_type_name || 'Unknown',
    subCategory: options.extractSubCategory ? options.extractSubCategory(apiPoi) : (apiPoi.subcategory || ''),
    originalCategory: apiPoi.ifc_category,
    system: apiPoi.poi_group_name || '',
    location: apiPoi.ifc_source_file || '',
    status: 'active',
    metadata: (() => {
      try {
        return apiPoi.custom_data ? JSON.parse(apiPoi.custom_data) : {};
      } catch {
        return {};
      }
    })(),
    ifcElementGuid: apiPoi.ifc_element_guid || undefined,
    ifcElementExpressId: apiPoi.ifc_element_express_id || undefined,
    ...(options.extraProperties || {})
  },
  visualization: {
    icon: apiPoi.icon || '📍',
    color: options.getColor ? options.getColor(apiPoi) : '#48CAE4',
    size: 12,
    visibility: apiPoi.visibility_check !== false
  }
});
